import {
  Component,
  Input,
  OnInit,
  forwardRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

import { Store } from '@ngrx/store';

import { toUnix } from '../../utils/date-utils';
import { AppState } from 'src/app/reducers';
import { selectCompanyDateBounds } from 'src/app/core/reducers';

export interface DateRangeValue {
  startTimestamp: number;
  endTimestamp: number;
}

@Component({
  selector: 'app-form-date-range-picker',
  templateUrl: './form-date-range-picker.component.html',
  styleUrls: ['./form-date-range-picker.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDateRangePickerComponent),
      multi: true,
    },
  ],
})
export class FormDateRangePickerComponent
  implements ControlValueAccessor, OnInit
{
  @Input() formGroup: FormGroup;
  @Input() startPropertyName: string;
  @Input() endPropertyName: string;
  @Input() presetBar: boolean = true;
  @Input() timeDetails: boolean = true;

  fromDate: Date = new Date();
  toDate: Date = new Date();

  private onChange: (value: DateRangeValue) => void = () => {};
  private onTouched: () => void = () => {};

  private store: Store<AppState> = inject(Store);
  companyBounds$ = this.store.select(selectCompanyDateBounds);

  ngOnInit(): void {
    if (this.formGroup && this.startPropertyName && this.endPropertyName) {
      const startControl = this.formGroup.get([this.startPropertyName]);
      const endControl = this.formGroup.get([this.endPropertyName]);

      if (startControl?.value) {
        this.fromDate = new Date(startControl.value * 1000);
      }
      if (endControl?.value) {
        this.toDate = new Date(endControl.value * 1000);
      }
    }
  }

  onDateRangeSelection(range: { from: Date; to: Date }): void {
    this.fromDate = range.from;
    this.toDate = range.to;

    const value: DateRangeValue = {
      startTimestamp: toUnix(this.fromDate),
      endTimestamp: toUnix(this.toDate),
    };

    this.onChange(value);
    this.onTouched();

    if (this.formGroup && this.startPropertyName && this.endPropertyName) {
      this.formGroup.patchValue({
        [this.startPropertyName]: value.startTimestamp,
        [this.endPropertyName]: value.endTimestamp,
      });
    }
  }

  writeValue(value: DateRangeValue): void {
    if (value) {
      this.fromDate = new Date(value.startTimestamp * 1000);
      this.toDate = new Date(value.endTimestamp * 1000);
    }
  }

  registerOnChange(fn: (value: DateRangeValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
